// ============================================================================
// Inline icon set for the app shell + dashboard. No icon dependency — these are
// hand-tuned to the Pulse stroke weight and inherit `currentColor`.
// ============================================================================
import type { SVGProps } from "react";

type IconProps = SVGProps<SVGSVGElement>;

const Svg = (props: IconProps) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden width="20" height="20" {...props} />
);

export const IconUsers = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="9" cy="8" r="3.2" stroke="currentColor" strokeWidth="2" />
    <path d="M3.5 19a5.5 5.5 0 0 1 11 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M16 5.5a3 3 0 0 1 0 6M17 19a5.5 5.5 0 0 0-3-4.9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </Svg>
);

export const IconCalendar = (p: IconProps) => (
  <Svg {...p}>
    <rect x="3.5" y="5" width="17" height="15" rx="3" stroke="currentColor" strokeWidth="2" />
    <path d="M3.5 9.5h17M8 3v3M16 3v3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </Svg>
);

export const IconClock = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="2" />
    <path d="M12 7.5V12l3 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

export const IconBellAlert = (p: IconProps) => (
  <Svg {...p}>
    <path d="M6 9a6 6 0 0 1 12 0c0 4 1.2 5.5 2 6.5H4c.8-1 2-2.5 2-6.5Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    <path d="M9.5 19a2.5 2.5 0 0 0 5 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </Svg>
);

export const IconStar = (p: IconProps & { filled?: boolean }) => {
  const { filled, ...rest } = p;
  return (
    <Svg {...rest}>
      <path
        d="m12 3.5 2.6 5.3 5.9.86-4.25 4.14 1 5.85L12 17.9l-5.25 2.76 1-5.85L3.5 9.66l5.9-.86L12 3.5Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
        fill={filled ? "currentColor" : "none"}
      />
    </Svg>
  );
};

export const IconArrowUp = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 19V6m0 0-5 5m5-5 5 5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

export const IconArrowDown = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 5v13m0 0 5-5m-5 5-5-5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

export const IconMinus = (p: IconProps) => (
  <Svg {...p}>
    <path d="M6 12h12" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
  </Svg>
);

export const IconChevronLeft = (p: IconProps) => (
  <Svg {...p}>
    <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

export const IconChevronRight = (p: IconProps) => (
  <Svg {...p}>
    <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

export const IconPlus = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
  </Svg>
);

export const IconMapPin = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 21s6.5-5.2 6.5-10A6.5 6.5 0 0 0 5.5 11c0 4.8 6.5 10 6.5 10Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    <circle cx="12" cy="11" r="2.3" stroke="currentColor" strokeWidth="2" />
  </Svg>
);

export const IconLayoutGrid = (p: IconProps) => (
  <Svg {...p}>
    <rect x="4" y="4" width="7" height="7" rx="1.8" stroke="currentColor" strokeWidth="2" />
    <rect x="13" y="4" width="7" height="7" rx="1.8" stroke="currentColor" strokeWidth="2" />
    <rect x="4" y="13" width="7" height="7" rx="1.8" stroke="currentColor" strokeWidth="2" />
    <rect x="13" y="13" width="7" height="7" rx="1.8" stroke="currentColor" strokeWidth="2" />
  </Svg>
);

export const IconMessage = (p: IconProps) => (
  <Svg {...p}>
    <path d="M5 5h14a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H9l-4 3v-3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
  </Svg>
);
